/**
 * Device Fingerprint Generator
 * 
 * Generates a stable device fingerprint by combining multiple browser signals:
 * - Canvas rendering (GPU/font differences)
 * - WebGL renderer info
 * - Navigator properties (platform, language, cores, memory)
 * - Screen properties
 * - Timezone
 * 
 * The fingerprint is hashed client-side and sent to the server.
 * The server stores a SHA-256 hash of this hash, so the raw fingerprint
 * is never stored anywhere.
 */

async function sha256(message) {
  const encoder = new TextEncoder()
  const data = encoder.encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 200
    canvas.height = 50
    const ctx = canvas.getContext('2d')
    if (!ctx) return 'no-canvas'

    // Draw text with specific font and style
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillStyle = '#f60'
    ctx.fillRect(125, 1, 62, 20)
    ctx.fillStyle = '#069'
    ctx.fillText('Odland 🏔️', 2, 15)
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.fillText('Onboarding', 4, 30)

    // Draw shapes
    ctx.beginPath()
    ctx.arc(50, 25, 10, 0, Math.PI * 2)
    ctx.fillStyle = '#3366cc'
    ctx.fill()

    return canvas.toDataURL()
  } catch {
    return 'canvas-error'
  }
}

function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    if (!gl) return 'no-webgl'

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
    if (!debugInfo) return 'no-debug-info'

    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    return `${vendor}~${renderer}`
  } catch {
    return 'webgl-error'
  }
}

function getNavigatorSignals() {
  const nav = navigator
  return [
    nav.platform || 'unknown',
    nav.language || 'unknown',
    nav.hardwareConcurrency || 0,
    nav.deviceMemory || 0,
    nav.maxTouchPoints || 0,
    nav.cookieEnabled ? 1 : 0,
    nav.pdfViewerEnabled ? 1 : 0,
  ].join('|')
}

function getScreenSignals() {
  const s = screen
  return [
    s.width,
    s.height,
    s.colorDepth,
    s.pixelDepth,
    window.devicePixelRatio || 1,
  ].join('|')
}

function getTimezoneSignal() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return String(new Date().getTimezoneOffset())
  }
}

function getInstalledPlugins() {
  try {
    if (!navigator.plugins || navigator.plugins.length === 0) return 'no-plugins'
    return Array.from(navigator.plugins)
      .slice(0, 10)
      .map(p => p.name)
      .join(',')
  } catch {
    return 'plugins-error'
  }
}

/**
 * Generate a device fingerprint hash.
 * Returns a hex string (SHA-256).
 * This is deterministic for the same device/browser.
 */
export async function generateFingerprint() {
  const signals = [
    getCanvasFingerprint(),
    getWebGLFingerprint(),
    getNavigatorSignals(),
    getScreenSignals(),
    getTimezoneSignal(),
    getInstalledPlugins(),
  ]

  const raw = signals.join('|||')
  const hash = await sha256(raw)
  return hash
}

/**
 * Get or create a cached fingerprint.
 * Stores in sessionStorage so it's consistent across the wizard steps
 * but doesn't persist across sessions.
 */
export async function getFingerprint() {
  const CACHE_KEY = 'odland_onboard_fp'
  const cached = sessionStorage.getItem(CACHE_KEY)
  if (cached) return cached

  const fp = await generateFingerprint()
  sessionStorage.setItem(CACHE_KEY, fp)
  return fp
}